// src/axiosInstance.js
import axios from 'axios';
import { toast } from 'react-toastify';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 15000,
});

// Simple UUID v4 generator fallback
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const idempotencyCache = new Map();

// Generate a simple string hash
const generateHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString();
};

// Request Interceptor: Attaches Idempotency-Key for POST/PUT
axiosInstance.interceptors.request.use(
  (config) => {
    if (config.method === 'post' || config.method === 'put') {
      let payloadString = '';
      if (config.data instanceof FormData) {
        const entries = [];
        for (let [key, value] of config.data.entries()) {
          if (value instanceof File) {
            entries.push(`${key}:${value.name}-${value.size}`);
          } else {
            entries.push(`${key}:${value}`);
          }
        }
        payloadString = entries.join('|');
      } else {
        payloadString = typeof config.data === 'string' ? config.data : JSON.stringify(config.data || {});
      }
      
      const hashKey = generateHash(`${config.method}-${config.url}-${payloadString}`);
      
      let idempotencyKey;
      if (idempotencyCache.has(hashKey)) {
        idempotencyKey = idempotencyCache.get(hashKey).key;
      } else {
        idempotencyKey = generateUUID();
        idempotencyCache.set(hashKey, { key: idempotencyKey, timestamp: Date.now() });
      }

      // Cleanup cache older than 2 minutes
      for (const [k, v] of idempotencyCache.entries()) {
        if (Date.now() - v.timestamp > 2 * 60 * 1000) {
          idempotencyCache.delete(k);
        }
      }

      config.headers['Idempotency-Key'] = idempotencyKey;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If it's a 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't refresh for auth routes to avoid infinite loops
      const authRoutes = ['/auth/login', '/auth/signup', '/auth/refresh', '/auth/logout'];
      const isAuthRoute = authRoutes.some(route => originalRequest.url.includes(route));

      if (isAuthRoute) {
        return Promise.reject(error);
      }

      const hasSession = localStorage.getItem("ritika_session_active") === "true";
      if (!hasSession) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => axiosInstance(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        await axios({
          method: 'post',
          url: `${API_BASE_URL}/auth/refresh`,
          withCredentials: true,
        });

        isRefreshing = false;
        processQueue(null);

        // Clear idempotency cache so retried request gets a fresh key
        idempotencyCache.clear();
        // Remove the stale Idempotency-Key header so interceptor generates a new one
        delete originalRequest.headers['Idempotency-Key'];

        // Retry the original request with fresh config
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        localStorage.removeItem("ritika_session_active");
        processQueue(new Error('AUTHENTICATION_FAILED'));
        return Promise.reject(new Error('AUTHENTICATION_FAILED'));
      }
    }

    // Handle other errors
    if (error.response?.status !== 401 && !error.config?.suppressToast) {
      const status = error.response?.status;
      const isServerError = status >= 500;
      
      const message = isServerError 
        ? 'The server encountered an issue. Please try again in a few moments.'
        : (error.response?.data?.message || error.message || 'Something went wrong');

      if (isServerError) {
        toast.warn(message, { icon: 'ℹ️' }); // Use warn/info for 500 to be less "terrifying"
      } else {
        toast.error(message);
      }
    }

    if (error.response?.status === 403 && !error.config?.suppressToast) {
      toast.warn('You do not have permission to perform this action.');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
